import _ from "lodash";
import moment from "moment-timezone";
import { graphql } from "react-relay";
import { v4 as uuidv4 } from "uuid";

import type { missions_scheduledMission$data } from "__generated__/missions_scheduledMission.graphql";

export type StartTriggers = NonNullable<missions_scheduledMission$data["startTriggers"]>;
export type EndTriggers = missions_scheduledMission$data["endTriggers"];

export type Vehicle = missions_scheduledMission$data["vehicle"];

export const scheduledMissionFragment = graphql`
  fragment missions_scheduledMission on ScheduledMission @inline {
    active
    id
    uuid
    template {
      id
      uuid
      name
      timezone
      currentSpec {
        numWaypoints
        missionType
        estimatedDuration
        ncpgFileId
        ncpgFile {
          id
          isUploaded
          kind
        }
        takeoffPoint {
          siteId
          site {
            name
          }
        }
      }
    }
    dock {
      displayName
      dockId
    }
    vehicle {
      displayName
      vehicleId
      scheduledMissionsDisabled
    }
    startTriggersJson
    endTriggersJson
    startTriggers {
      uuid
      expression {
        logic
        predicates {
          uuid
          condition {
            weeklyCalendarEvent {
              calendarEvent {
                dayOfWeek
                hourOfDay
                minuteOfHour
              }
              maxTimeDelaySeconds
            }
          }
        }
      }
    }
    endTriggers {
      condition {
        batteryPercentage {
          valueCondition {
            lessThan {
              value
            }
          }
        }
      }
    }
  }
`;

// Convert back to an action that the backend can serialize to protobuf
interface MissionSchedulerParams {
  daysOfWeek: number[];
  timesOfDay: Date[];
  templateUuid: string;
  batteryThreshold?: number;
  dockId?: string;
  vehicleId?: string;
  timezone: string;
}

const CALENDAR_EVENT_MAX_TIME_DELAY = 300; // seconds
export const SCHEDULE_TIME_FORMAT = "hh:mm a e";

const parseClockTime = (time: Date, dayOfWeek: number, timezone: string) => {
  // Parse in the provided time zone
  const asMoment = moment.tz(time, timezone);
  asMoment.set("day", dayOfWeek);
  // Shift to UTC
  asMoment.utc();
  // Return component parts to enter into predicate structure
  return { hour: asMoment.hour(), minute: asMoment.minute(), weekday: asMoment.weekday() };
};

interface Expression {
  logic: "OR" | "AND";
  predicates: Predicate[];
}

type Predicate =
  | {
      uuid: string;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      condition: any;
    }
  | {
      uuid: string;
      expression: Expression;
    };

interface ScheduleMutationParams
  extends Pick<MissionSchedulerParams, "templateUuid" | "dockId" | "vehicleId" | "timezone"> {
  startTriggers: string;
  endTriggers?: string;
}

export const buildScheduledMission = ({
  daysOfWeek,
  timesOfDay,
  timezone,
  batteryThreshold,
  ...params
}: MissionSchedulerParams): ScheduleMutationParams => {
  const startTriggers: Predicate = {
    uuid: uuidv4(),
    expression: {
      logic: "OR",
      // We just OR together every pairing of date and time from the input parameters
      predicates: _.flatten(
        daysOfWeek.map(day =>
          timesOfDay.map(time => {
            const { hour, minute, weekday } = parseClockTime(time, day, timezone);
            return {
              uuid: uuidv4(),
              condition: {
                weeklyCalendarEvent: {
                  calendarEvent: {
                    dayOfWeek: weekday,
                    hourOfDay: hour,
                    minuteOfHour: minute,
                  },
                  maxTimeDelaySeconds: CALENDAR_EVENT_MAX_TIME_DELAY,
                },
              },
            };
          })
        )
      ),
    },
  };

  const scheduledMission: ScheduleMutationParams = {
    ...params,
    timezone,
    startTriggers: JSON.stringify(startTriggers),
  };

  if (batteryThreshold) {
    const endTriggers: Predicate = {
      uuid: uuidv4(),
      condition: {
        batteryPercentage: {
          valueCondition: {
            lessThan: batteryThreshold,
          },
        },
      },
    };
    scheduledMission.endTriggers = JSON.stringify(endTriggers);
  }

  return scheduledMission;
};

export const parseStartTriggers = (predicate: StartTriggers, timezone: string | null) => {
  const days = new Set<number>();
  const times = new Set<string>();
  for (const calendarPredicate of predicate.expression!.predicates!) {
    if (calendarPredicate?.condition?.weeklyCalendarEvent) {
      const { dayOfWeek, hourOfDay, minuteOfHour } =
        calendarPredicate.condition.weeklyCalendarEvent.calendarEvent!;
      // We construct a moment object based on the UTC hour/minute/weekday from the predicate, then
      // shift it to the provided time zone to populate the fields in the scheduling UI
      const utcTimestamp = moment.utc(`${hourOfDay}:${minuteOfHour} ${dayOfWeek}`, "kk:mm e");
      const timestamp = timezone ? utcTimestamp.tz(timezone) : utcTimestamp.local();
      days.add(timestamp.weekday());
      times.add(timestamp.format("h:mm A"));
    } else {
      return { days: [], times: [] };
    }
  }
  return { days: Array.from(days), times: Array.from(times) };
};

export const checkIsFlyToPointMission = (missionName?: string) => {
  return missionName?.startsWith("Fly to Point - ");
};
